import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ActionType, ActionPriority } from '../types';
import { actionService } from '../actionService';
import { getActiveSessionOrganizationCode } from '../../../../shared/api';

interface CreateActionModalProps {
  onClose: () => void;
  onCreated: () => void;
}

export const CreateActionModal: React.FC<CreateActionModalProps> = ({ onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [actionType, setActionType] = useState<ActionType>('CORRECTIVE');
  const [priority, setPriority] = useState<ActionPriority>('HIGH');
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });
  const [department, setDepartment] = useState('Flight Operations');
  const [assigneeName, setAssigneeName] = useState('Chief Pilot');
  const [sourceModule, setSourceModule] = useState('INVESTIGATION');
  const [sourceReference, setSourceReference] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !dueDate) {
      alert('Action title and target due date are required.');
      return;
    }

    try {
      setLoading(true);
      await actionService.createAction({
        organizationId: getActiveSessionOrganizationCode() || undefined,
        title: title.trim(),
        description: description.trim() || undefined,
        actionType,
        priority,
        dueDate: new Date(dueDate).toISOString(),
        department: department.trim() || undefined,
        assigneeName: assigneeName.trim() || undefined,
        assigneeId: assigneeName.trim() ? `USR-${Date.now().toString().slice(-4)}` : undefined,
        sourceModule: sourceModule || undefined,
        sourceReference: sourceReference.trim() || undefined,
        createdBy: 'SAFETY_OFFICER',
      });
      onCreated();
    } catch (err: any) {
      alert(`Failed to create action: ${err.message || 'Error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Create Safety Action</h3>
            <p>
              Corrective, Preventive & Containment Action Lifecycle Management
            </p>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
              Action Title *
            </label>
            <input
              type="text"
              required
              placeholder="e.g., Update Standard Operating Procedures for Dual Bleed Warning Alert"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
              Description & Detailed Scope of Implementation
            </label>
            <textarea
              rows={3}
              placeholder="Specify the engineering, training, or procedural corrective steps required..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Action Type
              </label>
              <select
                value={actionType}
                onChange={(e) => setActionType(e.target.value as ActionType)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  background: '#ffffff',
                }}
              >
                <option value="CORRECTIVE">CORRECTIVE (Fix Root Cause)</option>
                <option value="PREVENTIVE">PREVENTIVE (Prevent Recurrence)</option>
                <option value="CONTAINMENT">CONTAINMENT (Immediate Barrier)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Priority
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as ActionPriority)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  background: '#ffffff',
                }}
              >
                <option value="IMMEDIATE">IMMEDIATE (Critical Safety)</option>
                <option value="HIGH">HIGH (Urgent)</option>
                <option value="MEDIUM">MEDIUM (Standard)</option>
                <option value="LOW">LOW (Routine)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Target Due Date *
              </label>
              <input
                type="date"
                required
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Responsible Department
              </label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  background: '#ffffff',
                }}
              >
                <option value="Flight Operations">Flight Operations</option>
                <option value="Ground Operations">Ground Operations</option>
                <option value="Maintenance & Engineering">Maintenance & Engineering</option>
                <option value="Cabin Crew Operations">Cabin Crew Operations</option>
                <option value="Safety & Quality">Safety & Quality</option>
                <option value="Dispatch & OCC">Dispatch & OCC</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Assignee Name
              </label>
              <input
                type="text"
                placeholder="Person responsible for execution"
                value={assigneeName}
                onChange={(e) => setAssigneeName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Source Module
              </label>
              <select
                value={sourceModule}
                onChange={(e) => setSourceModule(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  background: '#ffffff',
                }}
              >
                <option value="INVESTIGATION">Investigation Recommendation</option>
                <option value="OCCURRENCE">Occurrence Follow-up</option>
                <option value="HAZARD">Hazard Control Barrier</option>
                <option value="RISK">Risk Mitigation Plan</option>
                <option value="AUDIT">Quality Audit Finding / CAPA</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Source Reference Ref No
              </label>
              <input
                type="text"
                placeholder="e.g. INV-2026-00001 or RSK-2026-00002"
                value={sourceReference}
                onChange={(e) => setSourceReference(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 18px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                color: '#475569',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 22px',
                borderRadius: '6px',
                border: 'none',
                background: '#0284c7',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Creating Action...' : 'Create Safety Action'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
